import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    debounce,
    FormControl,
    InputAdornment,
    LinearProgress,
    List,
    ListSubheader,
    OutlinedInput,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import ArticleListItem from './article-list-item.component';
import { searchHelpTemplate } from 'apollo/templates/zendesk';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { HelpArticle } from 'models/zenddesk';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';

type HelpSearchProps = {
    onArticleClicked: (articleId: string) => void;
    contextArticles: HelpArticle[];
};
const HelpSearch: FC<HelpSearchProps> = ({ onArticleClicked, contextArticles }: HelpSearchProps) => {
    const { t: translate } = useTranslation(['common', 'help-drawer']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [searchResults, setSearchResults] = useState<HelpArticle[]>([]);
    const [articlesForCurrentView, setArticlesForCurrentView] = useState<HelpArticle[]>(contextArticles);
    const [searchText, setSearchText] = useState('');
    const [isSearchCompleted, setIsSearchCompleted] = useState(false);
    const [searchTags, setSearchTags] = useState<string[]>([]);

    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();

    useEffect(() => {
        const tags = [];
        if (permissions.isLabTechnician(currentUser.type)) {
            tags.push('technician-user');
        } else if (permissions.isDentist(currentUser.type)) {
            tags.push('dentist-user');
            const orderType = customerSettings.getCustomerSetting(
                currentOrg.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ORDER_VERSION,
            );
            tags.push(orderType.toLowerCase());
        }
        if (currentUser.role.includes('ADMIN')) {
            tags.push('admin-user');
        } else {
            tags.push('normal-user');
        }
        setSearchTags(tags);
    }, []);

    const [performSearchHelp, { loading }] = useLazyQuery(searchHelpTemplate, {
        onCompleted({ searchHelp }) {
            setSearchResults(searchHelp);
            setIsSearchCompleted(true);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setArticlesForCurrentView(contextArticles);
    }, [contextArticles]);

    const performSearch = useCallback(
        debounce((query: string) => {
            performSearchHelp({ variables: { query, tags: searchTags } });
        }, 800),
        [searchTags],
    );

    const handleSearchInput = (query: string) => {
        setIsSearchCompleted(false);
        setSearchText(query);
        if (!query) {
            setSearchResults([]);
        } else {
            performSearch(query);
        }
    };

    const listTitle = () => {
        return searchResults.length > 0
            ? translate('help-drawer:SEARCH_RESULTS')
            : translate('help-drawer:HELP_ARTICLES_FOR_CURRENT_VIEW');
    };

    const showResults = () => {
        return !loading && (searchResults.length > 0 || articlesForCurrentView.length > 0);
    };

    return (
        <Fragment>
            <Box>
                <Box mt={1} mb={2}>
                    <FormControl variant="outlined" fullWidth>
                        <OutlinedInput
                            value={searchText}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            }
                            placeholder={translate('help-drawer:SEARCH_HELP_DB')}
                        />
                    </FormControl>
                </Box>
                {loading ? <LinearProgress color="secondary" /> : undefined}

                {isSearchCompleted && searchResults.length === 0 ? (
                    <Box mb={2}>
                        <Alert severity="info">{translate('help-drawer:NO_ARTICLES_FOUND')}</Alert>
                    </Box>
                ) : undefined}

                {showResults() ? (
                    <Paper variant="outlined">
                        <Box>
                            <List>
                                <ListSubheader disableSticky>{listTitle()}</ListSubheader>
                                {searchResults.length > 0 ? (
                                    <Fragment>
                                        {searchResults.map((article) => (
                                            <ArticleListItem
                                                key={article.articleId}
                                                article={article}
                                                onArticleClicked={onArticleClicked}
                                            />
                                        ))}
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        {articlesForCurrentView.map((article) => (
                                            <ArticleListItem
                                                key={article.articleId}
                                                article={article}
                                                onArticleClicked={onArticleClicked}
                                            />
                                        ))}
                                    </Fragment>
                                )}
                            </List>
                        </Box>
                    </Paper>
                ) : undefined}
            </Box>
        </Fragment>
    );
};

export default HelpSearch;
